/**
 * Custom Searchbar Mobile Behavior
 * Handles the expand/collapse functionality on mobile devices
 */

class CustomSearchbarMobile {
  constructor() {
    this.searchbarContainer = document.querySelector('.custom-native-predictive-searchbar');
    this.mobileIcon = null;
    this.searchForm = null;
    this.searchInput = null;
    this.isExpanded = false;
    
    if (this.searchbarContainer) {
      this.init();
    }
  }

  init() {
    this.createMobileStructure();
    this.setupEventListeners();
    this.handleResize();
    
    window.addEventListener('resize', this.debounce(this.handleResize.bind(this), 250));
  }

  createMobileStructure() {
    this.mobileIcon = document.createElement('div');
    this.mobileIcon.className = 'custom-searchbar__mobile-toggle';
    this.mobileIcon.innerHTML = `
      <span class="svg-wrapper">
          <svg fill="none" class="icon icon-search" viewBox="0 0 18 19"><path fill="currentColor" fill-rule="evenodd" d="M11.03 11.68A5.784 5.784 0 1 1 2.85 3.5a5.784 5.784 0 0 1 8.18 8.18m.26 1.12a6.78 6.78 0 1 1 .72-.7l5.4 5.4a.5.5 0 1 1-.71.7z" clip-rule="evenodd"/></svg>
      </span>
    `;
    this.mobileIcon.setAttribute('aria-label', 'Open search');
    this.mobileIcon.setAttribute('role', 'button');
    this.mobileIcon.setAttribute('tabindex', '0');
    this.searchForm = this.searchbarContainer.querySelector('.custom-searchbar__form');
    this.searchInput = this.searchbarContainer.querySelector('.custom-searchbar__input');
    this.createCloseButton();
    this.searchbarContainer.insertBefore(this.mobileIcon, this.searchForm);
    this.searchbarContainer.classList.add('custom-searchbar--mobile-ready');
  }

  createCloseButton() {
    const closeButton = document.createElement('button');
    closeButton.className = 'custom-searchbar__close-mobile';
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', 'Close search');
    closeButton.innerHTML = `
      <span class="svg-wrapper">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" viewBox="0 0 18 17"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"/></svg>
      </span>
    `;

    const formContainer = this.searchForm.querySelector('form');
    const firstDiv = formContainer.querySelector('div');
    firstDiv.appendChild(closeButton);

    closeButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.collapse();
    });
  }

  setupEventListeners() {
    this.mobileIcon.addEventListener('click', this.handleMobileIconClick.bind(this));
    this.mobileIcon.addEventListener('keydown', this.handleMobileIconKeydown.bind(this));

    document.addEventListener('click', this.handleOutsideClick.bind(this));
    document.addEventListener('keydown', this.handleEscapeKey.bind(this));

    const form = this.searchForm.querySelector('form');
    if (form) {
      form.addEventListener('submit', this.handleFormSubmit.bind(this));
    }

    if (this.searchInput) {
      this.searchInput.addEventListener('focus', () => {
        if (this.isMobile() && !this.isExpanded) {
          this.expand();
        }
      });
    }
  }

  handleMobileIconClick(e) {
    e.preventDefault();
    e.stopPropagation();
    this.expand();
  }

  handleMobileIconKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.expand();
    }
  }

  handleOutsideClick(e) {
    if (this.isExpanded && this.isMobile() && 
        !this.searchbarContainer.contains(e.target)) {
      this.collapse();
    }
  }

  handleEscapeKey(e) {
    if (e.key === 'Escape' && this.isExpanded && this.isMobile()) {
      this.collapse();
    }
  }

  handleFormSubmit(e) {
    if (this.isMobile()) {
      setTimeout(() => {
        this.collapse();
      }, 100);
    }
  }

  expand() {
    if (!this.isMobile() || this.isExpanded) return;

    this.searchbarContainer.classList.add('custom-searchbar--expanded');
    this.isExpanded = true;
    
    document.body.style.overflow = 'hidden';
    document.body.classList.add('custom-searchbar-mobile-open');
    
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.focus();
      }
    }, 300);

    this.mobileIcon.setAttribute('aria-expanded', 'true');
  }

  collapse() {
    if (!this.isMobile() || !this.isExpanded) return;

    this.searchbarContainer.classList.remove('custom-searchbar--expanded');
    this.isExpanded = false;

    document.body.style.overflow = '';
    document.body.classList.remove('custom-searchbar-mobile-open');

    if (this.searchInput) {
      this.searchInput.value = '';
      this.searchInput.blur();
    }

    const predictiveSearch = this.searchForm.querySelector('predictive-search');
    if (predictiveSearch && predictiveSearch.close) {
      predictiveSearch.close(true);
    }

    this.mobileIcon.setAttribute('aria-expanded', 'false');

    if (this.searchInput) {
      this.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  handleResize() {
    if (!this.isMobile() && this.isExpanded) {
      this.searchbarContainer.classList.remove('custom-searchbar--expanded');
      this.isExpanded = false;
      
      document.body.style.overflow = '';
      document.body.classList.remove('custom-searchbar-mobile-open');
    }
  }

  isMobile() {
    return window.innerWidth <= 1024;
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CustomSearchbarMobile();
  });
} else {
  new CustomSearchbarMobile();
}